/**
 * 연식별 코호트 규모 — 생존 편향의 크기 (DESIGN §8.10[1]).
 *
 * **왜 이 막대가 필요한가.** 앞 그래프에서 2017년산의 가동률이 좋아 보여도,
 * 그 코호트가 6,930기밖에 남지 않았다면 그 값은 **살아남은 것들의 값**이다.
 * 철거된 노후기는 모집단에 없으므로 노후화의 영향이 체계적으로 과소추정된다.
 * 대수를 나란히 두지 않으면 이 편향이 보이지 않는다.
 *
 * **"그 해 보급량"이라고 읽히면 안 된다.** 이것은 지금까지 남아 있고 관측되는
 * 수다. 축 이름과 각주에 그렇게 쓴다 — 원본 보급 통계가 없으므로 생존율을
 * 계산할 수는 없고, 계산할 수 없다는 것도 함께 적는다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { fetchEnvelope } from '@/lib/api'
import { axisStyle, CHART_H } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { Envelope, RankingData, RankingItem, YearCohortsData } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

// 판정은 서버 한 곳에 있다 (`/metrics/year_cohorts`, §8.10.1). 옆 그래프와
// **같은 모집단**을 그려야 하므로 같은 판정을 쓴다 — 규칙을 여기 다시 쓰면 갈라진다.

export function AgingSize() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['metrics/ranking', 'aging-size', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<RankingData>(
        '/metrics/ranking',
        toQueryFilters(filters),
        { entity: 'year' },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  })

  const cq = useQuery({
    queryKey: ['metrics/year_cohorts'],
    queryFn: ({ signal }) =>
      fetchEnvelope<YearCohortsData>('/metrics/year_cohorts', { mode: 'standard' }, {}, signal),
    staleTime: 10 * 60 * 1000,
  })

  const env = q.data as Envelope<RankingData> | undefined
  const verdictOf = useMemo(() => {
    const m = new Map<string, { verdict: string; reason: string }>()
    for (const c of (cq.data as Envelope<YearCohortsData> | undefined)?.data.cohorts ?? []) {
      m.set(String(c.year), { verdict: c.verdict, reason: c.reason })
    }
    return m
  }, [cq.data])

  const { rows, sentinels, total } = useMemo(() => {
    const all: RankingItem[] = [...(env?.data.ranking ?? []), ...(env?.data.excluded ?? [])]
    const named = all.filter((r) => r.entity_id)
    const usable = (r: RankingItem) =>
      (verdictOf.get(r.entity_id)?.verdict ?? 'cohort') === 'cohort'
    return {
      rows: named.filter(usable).sort((a, b) => Number(a.entity_id) - Number(b.entity_id)),
      sentinels: named.filter((r) => !usable(r)),
      // 분모는 **모든** 연식이다 — 별도 관리분도 모집단에는 있다
      total: named.reduce((s, r) => s + r.n_chargers, 0),
    }
  }, [env, verdictOf])

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')

    return {
      grid: { left: 56, right: 14, top: 28, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params]
          const r = rows[(arr[0] as { dataIndex: number }).dataIndex]
          if (!r) return ''
          return `<b>${r.entity_id}년산</b><br/>${int(r.n_chargers)}기<br/>모집단의 ${pct(
            total > 0 ? r.n_chargers / total : null,
            1,
          )}`
        },
      },
      xAxis: {
        type: 'category',
        data: rows.map((r) => r.entity_id),
        name: '설치 연식',
        nameLocation: 'middle',
        nameGap: 24,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3 },
      },
      yAxis: {
        type: 'value',
        name: '지금 남아 관측되는 충전기 수',
        nameTextStyle: { fontSize: 10, color: ink3, align: 'left' },
        ...axisStyle(ink3, hairline),
        axisLabel: {
          fontSize: 10,
          color: ink3,
          formatter: (v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v)),
        },
      },
      series: [
        {
          type: 'bar',
          data: rows.map((r) => r.n_chargers),
          barCategoryGap: '22%',
          itemStyle: { color: c1, borderRadius: [4, 4, 0, 0] },
        },
      ],
    }
  }, [rows, total])

  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">설치 연식별 코호트 규모</caption>
        <thead>
          <tr>
            <th scope="col">연식</th>
            <th scope="col">남아 있는 수</th>
            <th scope="col">모집단 비중</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.entity_id}>
              <th scope="row">{r.entity_id}</th>
              <td className="num">{int(r.n_chargers)}</td>
              <td className="num">{pct(total > 0 ? r.n_chargers / total : null, 1)}</td>
            </tr>
          ))}
          {sentinels.map((r) => (
            <tr key={r.entity_id} className="is-na">
              <th scope="row">{r.entity_id}</th>
              <td className="num">{int(r.n_chargers)}</td>
              <td>{verdictOf.get(r.entity_id)?.reason ?? '나이 신호로 쓸 수 없음'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="연식별 코호트 규모"
      question="각 연식이 지금 몇 대 남아 관측되고 있는가"
      meta={env?.meta}
      hideCoverage
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={rows.length === 0}
      onRetry={() => void q.refetch()}
      encoding={[
        { role: '가로축', what: '설치 연식' },
        { role: '세로축', what: '지금 남아 관측되는 충전기 수 — 그 해 보급량이 아니다' },
      ]}
      reading="앞 그래프의 코호트별 값이 몇 대에서 나온 값인지를 여기서 확인합니다."
      notes={[
        {
          label: '보급량이 아니다',
          text:
            '이 막대는 지금까지 남아 있고 관측되는 수입니다. 그 해 몇 대가 설치됐는지는 이 데이터에 없으므로 생존율을 계산할 수 없습니다 — 계산하지 않습니다.',
        },
        {
          label: '왜 중요한가',
          text:
            '오래된 코호트의 좋은 가동률은 「살아남은 것들의 값」입니다. 고장난 것이 철거되어 모집단에서 빠졌다면 노후화의 영향은 체계적으로 과소추정됩니다.',
        },
        ...(sentinels.length > 0
          ? [
              {
                label: '별도 관리',
                text: `${sentinels.length}개 연식은 차트에서 빼고 표 보기에 사유와 함께 남겼습니다. ` +
                      '모집단 비중의 분모에는 그대로 들어갑니다 — 실재하는 설비이기 때문입니다.',
              },
            ]
          : []),
      ]}
      table={table}
    >
      <EChart option={option} height={CHART_H} ariaLabel="설치 연식별 코호트 규모 막대그래프" />
    </ChartFrame>
  )
}
